import { useState } from 'react';
import { 
  auto as followSystemColorScheme,
  disable as disableDarkReader,
  enable as enableDarkReader
} from 'darkreader';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAdjust }        from '@fortawesome/free-solid-svg-icons/faAdjust';
import { faMoon }          from '@fortawesome/free-solid-svg-icons/faMoon';
import { faSun }           from '@fortawesome/free-solid-svg-icons/faSun';

import './DarkModeButton.css';

const LOCAL_STORAGE_COLOR_SCHEME = 'color-scheme';
const COLOR_SCHEME_LIGHT = 'light';
const COLOR_SCHEME_DARK = 'dark';

function switchToDarkMode() {
  localStorage.setItem(LOCAL_STORAGE_COLOR_SCHEME, COLOR_SCHEME_DARK);
  followSystemColorScheme(false);
  enableDarkReader();
}

function switchToLightMode() {
  localStorage.setItem(LOCAL_STORAGE_COLOR_SCHEME, COLOR_SCHEME_LIGHT);
  followSystemColorScheme(false);
  disableDarkReader();
}

function switchToAutoMode() {
  localStorage.removeItem(LOCAL_STORAGE_COLOR_SCHEME);
  // DarkReader.auto: Follow the system DarkMode settings
  followSystemColorScheme(true);
}
  
/**
 * @returns a DarkMode button
 */
export default function DarkModeButton({ className, disabled }) {
  // Use React state to track the active color scheme so the button re-renders
  const [currentScheme, setCurrentScheme] = useState(() => {
    // Initialize state by checking for a saved color scheme preference
    const savedScheme = localStorage.getItem(LOCAL_STORAGE_COLOR_SCHEME);
    
    // Aplly the initial theme
    if (savedScheme === COLOR_SCHEME_DARK) switchToDarkMode();
    else if (savedScheme === COLOR_SCHEME_LIGHT) switchToLightMode();
    else switchToAutoMode();
    
    return savedScheme;
  });

  // Cycle through states: System (null) -> Dark -> Light -> System (null)
  const handleToggle = () => {
    if (!currentScheme) {
      switchToDarkMode();
      setCurrentScheme(COLOR_SCHEME_DARK);
    } 
    else if (currentScheme === COLOR_SCHEME_DARK) {
      switchToLightMode();
      setCurrentScheme(COLOR_SCHEME_LIGHT);
    } 
    else {
      switchToAutoMode();
      setCurrentScheme(null); // null represents system/auto mode
    }
  };

  return (
    <button 
      type="button"
      className={`dark-mode color-scheme ${className ?? ''}`} 
      disabled={disabled}
      onClick={handleToggle}
    >
      {/* Light Mode Active */}
      {currentScheme === COLOR_SCHEME_LIGHT && (
        <span className="color-scheme-light">
          <FontAwesomeIcon icon={faSun} />
        </span>
      )}

      {/* System/Auto Mode Active */}
      {!currentScheme && (
        <span className="color-scheme-auto">
          <FontAwesomeIcon icon={faAdjust} />
        </span>
      )}

      {/* Dark Mode Active */}
      {currentScheme === COLOR_SCHEME_DARK && (
        <span className="color-scheme-dark">
          <FontAwesomeIcon icon={faMoon} />
        </span>
      )}
    </button>
  );
}
